"use client";

import React, { useState } from "react";
import Link from "next/link";
import Twitter from "@/public/assets/icons/SocialMedia/Twitter.svg";
import Discord from "@/public/assets/icons/SocialMedia/Discord.svg";
import Instagram from "@/public/assets/icons/SocialMedia/Instagram.svg";
import Logo from "@/public/assets/icons/LeftSidebar/Logo.svg";
import { useToast } from "@/components/ui/use-toast";
import { Icons } from "@/components/(new)/shared/helper/Icons";

const Footer = () => {
  const footerLinks = [
    { name: "Disclaimer", href: "/disclaimer" },
    { name: "Legal", href: "/legal" },
    { name: "T&C", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Blogs", href: "/blog" },
    { name: "Pricing", href: "/pricing" },
  ];

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Subscribed Successfully! 🎉",
          description: "You've been successfully subscribed to our newsletter.",
          variant: "default",
          className: "font-medium border-2",
        });
        setEmail("");
      } else {
        const errorMessage =
          data.error?.message ||
          data.message ||
          "Failed to subscribe to newsletter";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Failed ❌",
        description:
          error instanceof Error
            ? error.message
            : "Failed to subscribe to newsletter",
        className: "font-medium border-2",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-black px-3 pt-3 pb-10 md:px-[45px]">
      <div className="mt-5 mb-[25px] flex w-full flex-col items-center justify-between gap-[45px] md:mb-[60px] md:flex-row md:items-start">
        <div className="flex flex-col items-center justify-start gap-[30px] max-md:w-full md:items-start">
          <div className="w-full max-w-[277px] justify-center text-sm font-medium text-[#BFBFBF] max-md:text-center md:max-w-[472px] md:text-[22px] md:leading-[29px]">
            Subscribe to stay updated with latest product updates
          </div>
          <div className="flex-center gap-[5px] max-md:w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="relative h-[50px] w-full overflow-hidden rounded-tl-md rounded-bl-md bg-[#f1f1f1] px-5 text-sm leading-normal font-medium text-[#808080] outline-1 outline-offset-[-1px] outline-white/5 md:h-[60px] md:w-[400px] md:text-base lg:w-[504px]"
              disabled={loading}
              autoComplete="email"
              autoCorrect="off"
              autoCapitalize="off"
              required
              placeholder="Enter your email"
            />
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-center aspect-square size-[50px] cursor-pointer overflow-hidden rounded-tr-md rounded-br-md bg-[#6cb4ff] outline-1 outline-offset-[-1px] outline-white/5 md:size-[60px]"
            >
              <Icons.arrow className="h-[18px] overflow-hidden text-black md:h-5" />
            </button>
          </div>
        </div>
        <div className="flex-center flex-wrap gap-[50px] px-5 font-normal max-md:hidden">
          {footerLinks.map((link) => (
            <Link
              href={link.href}
              key={link.href}
              className="text-base text-nowrap hover:underline"
            >
              {link.name}
            </Link>
          ))}
        </div>
        <div className="flex items-start justify-center gap-[50px] md:hidden">
          <div className="flex flex-col gap-[25px]">
            {footerLinks.slice(4, 6).map((link) => (
              <Link
                href={link.href}
                key={link.href}
                className="text-base text-nowrap hover:underline"
              >
                {link.name}
              </Link>
            ))}
          </div>
          <div className="flex flex-col gap-[25px]">
            {footerLinks.slice(0, 3).map((link) => (
              <Link
                href={link.href}
                key={link.href}
                className="text-base text-nowrap hover:underline"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-fit flex-col-reverse items-center justify-between max-md:gap-[25px] md:w-full md:flex-row">
        <Link href="/" className="flex-center gap-2.5 outline-none">
          <Logo className="h-7 md:h-[25px]" />
          <div className="font-worksans text-sm font-medium -tracking-[1px] md:text-xl md:-tracking-[1.1px]">
            Bundled.design
          </div>
        </Link>
        <div className="flex gap-[48px] max-md:w-full max-md:justify-between md:gap-10 md:pr-10">
          {[
            { href: "https://x.com/bundleddesign", Icon: Twitter },
            {
              href: "https://www.instagram.com/bundled.design",
              Icon: Instagram,
            },
            { href: "https://discord.gg/EQfgeJTjKK", Icon: Discord },
          ].map((link, index) => (
            <Link href={link.href} key={index}>
              <link.Icon className="size-[26px] transition-colors duration-200 md:size-[30px]" />
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
